import { useMemo } from "react";
import DOMPurify from "dompurify";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNotificationDetail } from "@/hooks/api/useNotifications";
import type { InAppNotification } from "@/api/types/notifications";

interface NotificationDetailModalProps {
  open: boolean;
  onClose: () => void;
  notification: InAppNotification | null;
}

export function extractEmailContent(html: string): string {
  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "div", "p", "span", "a", "br", "strong", "em",
      "table", "thead", "tbody", "tr", "th", "td",
      "style", "img", "h1", "h2", "h3", "ul", "ol", "li",
    ],
    ALLOWED_ATTR: ["class", "style", "href", "src", "alt", "width", "height"],
    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus"],
  });

  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitized, "text/html");

  // Extract the style tag to preserve original styling
  const styleTag = doc.querySelector("style");
  const styles = styleTag ? styleTag.outerHTML : "";

  // Find the content div and footer
  const contentDiv = doc.querySelector(".content");
  const footerDiv = doc.querySelector(".footer");

  if (contentDiv) {
    // Wrap in a container to apply the original email styles
    let result = styles;
    result += '<div class="email-body">';
    result += '<div class="content">' + contentDiv.innerHTML + "</div>";
    if (footerDiv) {
      result += footerDiv.outerHTML;
    }
    result += "</div>";
    return result;
  }

  return sanitized;
}

export function NotificationDetailModal({
  open,
  onClose,
  notification,
}: NotificationDetailModalProps) {
  const { data: detail, loading } = useNotificationDetail(
    open && notification ? notification.notification_date : null
  );

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  // Extract only the content section from email HTML
  const processedContent = useMemo(() => {
    if (!detail?.email_content) return null;
    return extractEmailContent(detail.email_content);
  }, [detail?.email_content]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-4xl max-h-[80vh] p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-lg font-semibold">
            {notification
              ? `Storm Impact Alert - ${formatDate(notification.notification_date)}`
              : "Notification Detail"}
          </DialogTitle>
          {notification && (
            <p className="text-sm text-muted-foreground mt-1">
              {notification.alert_count} Alert
              {notification.alert_count !== 1 ? "s" : ""} Triggered
            </p>
          )}
        </DialogHeader>

        <ScrollArea className="flex-1 max-h-[calc(80vh-120px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : processedContent ? (
            <div className="px-6 py-4">
              <div
                className="notification-content rounded-lg shadow-xl bg-white overflow-hidden"
                dangerouslySetInnerHTML={{ __html: processedContent }}
              />
            </div>
          ) : (
            <div className="px-6 py-12 text-center text-muted-foreground">
              <p>No content available</p>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export default NotificationDetailModal;
